import { isNil } from 'lodash';
import {
  isAddressComponent,
  isDateComponent,
  isEmailComponent,
  isNumberComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { AddressField } from './AddressField';
import { DateField } from './DateField';
import { EmailField } from './EmailField';
import { EditableField, Field } from './Field';
import { NumberField } from './NumberField';
import { RelatedEntityField } from './RelatedEntityField';
import { TagField } from './TagField';
import { TextField } from './TextField';

type LookupRefs = NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;

type LeafField = NumberField | DateField | TextField | AddressField | EmailField | TagField;

/**
 * A related card lookup rendered in a collection view. Holds the leaf value of the *first*
 * related card (resolved from the refs loaded with the list query, no extra fetching) plus
 * the total count of top-level related cards, so the collection view can render the first
 * value inline with a `+N` indicator. The full list — which may require fetching nested
 * cards — is resolved lazily by the overflow menu when the user opens it.
 */
export class RelatedLookupField implements EditableField {
  readonly fieldType = 'RelatedLookupField';
  readonly firstLeafField: LeafField | null;
  readonly leafComponent: API.AnyComponent | undefined;
  readonly count: number;
  readonly relatedField: RelatedEntityField | null;

  private constructor(
    firstLeafField: LeafField | null,
    leafComponent: API.AnyComponent | undefined,
    count: number,
    relatedField: RelatedEntityField | null,
  ) {
    this.firstLeafField = firstLeafField;
    this.leafComponent = leafComponent;
    this.count = count;
    this.relatedField = relatedField;
  }

  get isEmpty(): boolean {
    return this.count === 0 || isNil(this.firstLeafField) || this.firstLeafField.isEmpty;
  }

  validate(): string | null {
    return null;
  }

  isEqual(field: Field): boolean {
    if (!(field instanceof RelatedLookupField) || this.count !== field.count) {
      return false;
    }
    if (isNil(this.firstLeafField) || isNil(field.firstLeafField)) {
      return isNil(this.firstLeafField) && isNil(field.firstLeafField);
    }
    return this.firstLeafField.isEqual(field.firstLeafField);
  }

  toFieldJson(): null {
    return null;
  }

  toDisplayString(): string {
    return this.firstLeafField?.toDisplayString() ?? '';
  }

  static fromEntity(
    entity: API.WorkflowEntity,
    component: API.RelatedCardLookupComponent,
    refs: LookupRefs,
  ): RelatedLookupField | null {
    const count = relatedEntityIds(entity.fields?.[component.relatedCardComponentId]).length;
    if (count === 0) {
      return null;
    }

    const { leafComponent, leafDataList } = resolveRelatedLookup(entity, component, refs);
    const firstLeafField = isNil(leafComponent)
      ? null
      : firstNonEmptyLeaf(component, leafComponent, leafDataList);
    const relatedField = RelatedEntityField.fromFieldJson(
      entity.fields?.[component.relatedCardComponentId],
      refs.workflowEntityRefs,
      refs.workflowTemplateRefs,
    );

    return new RelatedLookupField(firstLeafField, leafComponent, count, relatedField);
  }
}

/**
 * Builds a leaf field for each resolvable card (in order) and returns the first non-empty one,
 * falling back to the first built field when every value is empty. Only cards present in the
 * refs can be inspected, so this is a best-effort "first value to show" — never a fetch.
 */
function firstNonEmptyLeaf(
  component: API.RelatedCardLookupComponent,
  leafComponent: API.AnyComponent,
  leafDataList: unknown[],
): LeafField | null {
  let firstBuilt: LeafField | null = null;
  for (const leafData of leafDataList) {
    const leafField = buildLeafField(component, leafComponent, leafData);
    if (isNil(leafField)) {
      continue;
    }
    if (isNil(firstBuilt)) {
      firstBuilt = leafField;
    }
    if (!leafField.isEmpty) {
      return leafField;
    }
  }
  return firstBuilt;
}

function buildLeafField(
  component: API.RelatedCardLookupComponent,
  leafComponent: API.AnyComponent,
  leafData: unknown,
): LeafField | null {
  if (isNumberComponent(leafComponent)) {
    return NumberField.fromFieldJson(leafData, leafComponent.format);
  }
  if (isDateComponent(leafComponent)) {
    return DateField.fromFieldJson(leafData);
  }
  if (isTextComponent(leafComponent)) {
    return TextField.fromFieldJson(leafData, { multiline: leafComponent.multiline });
  }
  if (isAddressComponent(leafComponent)) {
    return AddressField.fromFieldJson(leafData);
  }
  if (isEmailComponent(leafComponent)) {
    return EmailField.fromFieldJson(leafData);
  }
  if (isTagComponent(leafComponent)) {
    return TagField.fromFieldJson(
      leafData,
      component.id,
      leafComponent.options ?? [],
      leafComponent.multiselect ?? false,
      leafComponent.addAdditionalValues ?? false,
    );
  }
  return null;
}

/**
 * Resolves the leaf component + the ordered leaf values for a related card lookup. The inline
 * view renders a single value (the first non-empty one), but we return every resolvable value
 * so an empty first card doesn't hide a populated later one. Supports one extra hop: when the
 * looked-up component is itself a related card, it resolves the secondary lookup component on
 * the nested workflow and reads the values from the nested related cards. Only cards present in
 * the refs are inspected — this never fetches.
 */
function resolveRelatedLookup(
  entity: API.WorkflowEntity,
  component: API.RelatedCardLookupComponent,
  refs: LookupRefs,
): { leafComponent: API.AnyComponent | undefined; leafDataList: unknown[] } {
  // Top-level template -> related card component -> looked-up template + component.
  const topLevelWorkflowTemplate = refs.workflowTemplateRefs?.find(
    wft => wft.id === entity.workflowTemplateId,
  );
  const topLevelRelatedCardComponent = topLevelWorkflowTemplate?.components
    .filter(isRelatedCardComponent)
    .find(c => c.id === component.relatedCardComponentId);
  const lookupWorkflowTemplateId = topLevelRelatedCardComponent?.workflowTemplateId;
  const lookupWorkflowTemplate = refs.workflowTemplateRefs?.find(
    wft => wft.id === lookupWorkflowTemplateId,
  );
  const lookupComponent = lookupWorkflowTemplate?.components.find(
    c => c.id === component.lookupComponentId,
  );

  const relatedCardIds = relatedEntityIds(entity.fields?.[component.relatedCardComponentId]);

  // Level 1: read the value from each related card, in order.
  if (isNil(lookupComponent) || !isRelatedCardComponent(lookupComponent)) {
    const leafDataList = relatedCardIds.map(id => {
      const lookupEntity = refs.workflowEntityRefs?.find(we => we.id === id);
      return lookupEntity?.fields?.[component.lookupComponentId];
    });
    return { leafComponent: lookupComponent, leafDataList };
  }

  // Level 2: the looked-up component is a related card, so hop once more into the nested
  // workflow and read the values from each nested related card, in order.
  const secondaryLookupWorkflowTemplate = refs.workflowTemplateRefs?.find(
    wft => wft.id === lookupComponent.workflowTemplateId,
  );
  const secondaryLookupComponent = secondaryLookupWorkflowTemplate?.components.find(
    c => c.id === component.secondaryLookupComponentId,
  );
  const leafDataList = relatedCardIds.flatMap(id => {
    const lookupEntity = refs.workflowEntityRefs?.find(we => we.id === id);
    const secondaryCardIds = relatedEntityIds(lookupEntity?.fields?.[component.lookupComponentId]);
    return secondaryCardIds.map(secondaryId => {
      const secondaryLookupEntity = refs.workflowEntityRefs?.find(we => we.id === secondaryId);
      return secondaryLookupEntity?.fields?.[component.secondaryLookupComponentId ?? ''];
    });
  });

  return { leafComponent: secondaryLookupComponent, leafDataList };
}

function relatedEntityIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(item => item?.id).filter((id): id is string => typeof id === 'string');
}
